"use client";

import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useConsultation } from "../../../../context/ConsultationContext";
import Navbar from "../../../../components/navbar";
import Footer from "../../../../components/footer";
import ConsultationHeader from "../components/ConsultationHeader";
import styles from "../css/patient-overview.module.css";
import Demographics from "../components/Demographics";
import Conditions from "../components/Conditions";
import Medications from "../components/Medications";
import AllergiesOverview from "../components/AllergiesOverview";
import ObstetricsOverview from "../components/ObstetricsOverview";
import { getProfileByPatient } from "../../../../api/profile/create_profile.js";

const TABS = [
  { id: "demographics", label: "Demographics" },
  { id: "conditions", label: "Conditions" },
  { id: "medications", label: "Medications" },
  { id: "allergies", label: "Allergies" },
  { id: "obstetrics", label: "Obstetrics" },
];

export default function PatientOverview() {
  const navigate = useNavigate();
  const { consultationData, nextStep, goToStep, updateConsultationData } = useConsultation();
  const [activeTab, setActiveTab] = useState("demographics");

  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [profileData, setProfileData] = useState(null);

  // Set current step to 1 when component mounts
  useEffect(() => {
    goToStep(1);
  }, [goToStep]);

  useEffect(() => {
    const patientId = consultationData?.patientId;
    if (!patientId) return;
    let cancelled = false;

    const load = async () => {
      try {
        setLoading(true);
        setLoadError("");
        const data = await getProfileByPatient(patientId);
        if (cancelled) return;
        setProfileData(data);
      } catch (e) {
        if (cancelled) return;
        setLoadError(e?.response?.data?.error || e?.message || "Failed to load patient profile");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [consultationData?.patientId]);

  const patientFromProfile = profileData?.patient || null;
  const patient = {
    ...(patientFromProfile || consultationData?.patient || {}),
    profilenumber: profileData?.profile?.profilenumber ?? patientFromProfile?.profilenumber ?? consultationData?.patient?.profilenumber,
    medicalaidprovider: profileData?.mainmember?.medicalaidprovider ?? profileData?.mainmember?.medicalAidProvider,
    medicalaidnumber: profileData?.mainmember?.medicalaidnumber ?? profileData?.mainmember?.medicalAidNumber,
  };

  useEffect(() => {
    if (!patientFromProfile) return;
    updateConsultationData({ patient });
  }, [patientFromProfile]);

  const patientSex = patient?.sex || patient?.gender;
  const isMale = String(patientSex ?? "").trim().toLowerCase() === "male";
  const filteredTabs = isMale ? TABS.filter((tab) => tab.id !== "obstetrics") : TABS;

  const handleNext = () => {
    nextStep();
    navigate("/customer/clinic/medical-social-history");
  };

  const handlePrevious = () => {
    // No previous step for Patient Overview (first step)
    console.log("Patient Overview is the first step");
  };

  return (
    <div className={styles.pageWrapper}>
      <Navbar />

      <main className={styles.mainContent}>
        <div className={styles.container}>
          <ConsultationHeader 
            variant="patient-overview"
            currentStep={1}
            totalSteps={10}
            onPrevious={null}
            onNext={handleNext}
          />

          {/* Pill Tabs */}
          <div className={styles.tabList}>
            {filteredTabs.map((tab) => (
              <button
                key={tab.id}
                className={`${styles.tabButton} ${activeTab === tab.id ? styles.activeTab : ""}`}
                onClick={() => setActiveTab(tab.id)}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {/* Content Area */}
          <div className={styles.contentBox}>
            <div className={styles.tabContent}>
              {loading && <div className={styles.loader}>Loading...</div>}
              {!loading && loadError && <div className={styles.errorMessage}>{loadError}</div>}

              {!loading && !loadError && activeTab === "demographics" && <Demographics patientData={patient} />}
              {!loading && !loadError && activeTab === "conditions" && <Conditions patientData={patient} />}
              
              {/* Linked Medications Component */}
              {!loading && !loadError && activeTab === "medications" && <Medications patientData={patient} />}
              
              {/* Linked Allergies and Obstetrics */}
              {!loading && !loadError && activeTab === "allergies" && <AllergiesOverview patientData={patient} />}
              {!loading && !loadError && activeTab === "obstetrics" && <ObstetricsOverview patientData={patient} />}
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}